<template>
	<footer v-if="!isJapan" id="globalFooter" class="footer" :class="classes">
		<svgIcons class="hide"></svgIcons>
		<div class="footer-top">
			<div ref="select" v-click-outside="close" class="language" data-cy="lang-select">
				<div
					class="current-language"
					data-cy="lang-select-toggle"
					@click="toggle()"
					@keydown.esc.stop.prevent="close"
					@keydown.space.stop.prevent="toggle"
					@keydown.enter.stop.prevent="toggle"
				>
					<svgIcon icon-holder-class="icon-locale" icon-id="icon-globe" />
					<span>{{ $t(`footer.regions.${region}`) }}</span>
					<span v-if="region !== 'tw'">&nbsp;-&nbsp;{{ $t(`footer.languages.${$i18n.locale}`) }}</span>
					<svgIcon icon-holder-class="icon-footer-arrow" icon-id="icon-footer-arrow" />
				</div>

				<!-- language panel -->
				<transition name="slide">
					<ul v-if="showLanguage" class="languages list-unstyled">
						<li data-cy="panel-region-global" class="region global">
							<a v-if="region === 'global'" class="global" @click="toggleGlobalLocale">
								{{ $t('footer.regions.global') }}
								<svgIcon v-if="showGlobalLocale" icon-holder-class="icon-minus" icon-id="icon-minus" />
								<svgIcon v-if="!showGlobalLocale" icon-holder-class="icon-plus" icon-id="icon-plus" />
							</a>
							<a v-else class="region-selector" @click="changeRegion(regionHost.global, 'en', 'Global')">{{
								$t('footer.regions.global')
							}}</a>
						</li>

						<!-- global locale panel -->
						<transition name="slide">
							<ul v-if="region === 'global' && showGlobalLocale" class="global-locale list-unstyled">
								<li v-for="(languages, index) in language['global']" :key="`languages-${index}`" class="language-list">
									<a
										:data-cy="`select-lang-${languages.split('-')[0]}`"
										class="language-selector"
										:languageset="languagesInEnglish[languages.split('-')[0]]"
										@click="changeLanguage(languages.split('-')[0], languagesInEnglish[languages.split('-')[0]])"
										>{{ $t(`footer.languages.${languages.split('-')[0]}`) }}</a
									>
								</li>
							</ul>
						</transition>

						<li class="region th">
							<a class="th region-selector thailand" @click="changeRegion(regionHost.th, 'th', 'Thailand')">{{
								$t('footer.regions.th')
							}}</a>
						</li>
						<li class="region tw">
							<a class="tw region-selector taiwan" @click="changeRegion(regionHost.tw, 'zh', 'Taiwan')">{{
								$t('footer.regions.tw')
							}}</a>
						</li>
						<li class="region">
							<a class="sea region-selector sea" @click="changeRegion(regionHost.sea, 'en', 'SEA')">{{
								$t('footer.regions.sea')
							}}</a>
						</li>
						<li class="region kr">
							<a target="_blank" rel="external" class="kr region-selector korea" @click="redirectToKR">
								{{ $t('footer.regions.kr') }}
								<svgIcon icon-holder-class="icon-btn-link" icon-id="icon-btn-link" />
							</a>
						</li>
						<li class="region jp">
							<a :href="externalRegionLinks.jp" target="_blank" rel="external" class="jp region-selector japan">
								{{ $t('footer.regions.jp') }}
								<svgIcon icon-holder-class="icon-btn-link" icon-id="icon-btn-link" />
							</a>
						</li>
					</ul>
				</transition>
			</div>
			<div class="clearfix"></div>
		</div>
		<div class="footer-body">
			<ul class="links links-top list-inline text-uppercase">
				<li v-for="(footerTopLink, index) in footerTopLinks" :key="`footer-top-${index}`">
					<a
						:href="footerTopLink.url"
						:class="`footer-link footer-top-link ${camelize(footerTopLink.name)}`"
						@click="footerLinkTracking(footerTopLink.name)"
						>{{ footerTopLink.name }}</a
					>
				</li>
			</ul>
			<ul class="links links-bottom list-inline text-uppercase">
				<li v-for="(footerBottomLink, index) in footerBottomLinks[region]" :key="`footer-top-${index}`">
					<a
						:href="footerBottomLink.url"
						:class="footerBottomLink.name"
						class="footer-link"
						@click="footerLinkTracking(footerBottomLink.name)"
						>{{ $t(`footer.${footerBottomLink.name}`) }}</a
					>
				</li>
				<li id="otPrefCenter" :class="{ hidden: isGlobalPrefCenter || !isWest }">
					<!-- OneTrust Cookies Settings button start -->
					<a class="ot-sdk-show-settings">{{ $t('footer.cookieSettings') }}</a>
					<!-- OneTrust Cookies Settings button end -->
				</li>
			</ul>
			<div class="nexon-logo-footer">
				<a>
					<svgIcon icon-holder-class="logo" icon-id="nexon-logo-full" />
				</a>
			</div>
		</div>
		<div class="footer-bottom">
			<p class="copyright">{{ $t('footer.copyright', [currentYear]) }}</p>
		</div>
	</footer>
</template>

<script>
import svgIcons from '@arena/toolkit/src/components/common/icon-sprite';
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';
import clickOutside from '@arena/toolkit/src/utils/click-outside';
import config from '@/config/config-footer';

export default {
	name: 'NavFooter',
	components: {
		svgIcons,
		svgIcon,
	},
	directives: {
		clickOutside,
	},
	data() {
		return {
			currentYear: new Date().getFullYear(),
			showLanguage: false,
			showGlobalLocale: false,
			footerBottomLinks: config.footerBottomLinks,
			language: config.languages,
			externalRegionLinks: config.externalRegionLinks,
			regionHost: config.externalRegionLinks,
			languagesInEnglish: {
				es: 'Spanish',
				en: 'English',
				pt: 'Portuguese',
				de: 'German',
				fr: 'French',
				it: 'Italian',
				pl: 'Polish',
				tr: 'Turkish',
				th: 'Thai',
				zh: 'Chinese',
				nl: 'Dutch',
				ru: 'Russian',
			},
			otPrefCenterText: '',
			observer: null,
		};
	},
	computed: {
		classes() {
			return [this.region, this.currentLanguage, ...(this.hideFooter ? ['hideFooter'] : [])];
		},
		region() {
			return environment.region;
		},
		isJapan() {
			return this.region === 'japan';
		},
		isWest() {
			return this.region === 'global';
		},
		currentLanguage() {
			return this.$i18n.locale;
		},
		footerTopLinks() {
			return this.$store.state.footers.map((x) => ({
				name: this.findValueByPrefix(x.titles, this.$i18n.locale),
				url: this.findValueByPrefix(x.urls, this.$i18n.locale),
			}));
		},
		isGlobalPrefCenter() {
			return this.otPrefCenterText.toLowerCase().includes('cookie');
		},
		hideFooter() {
			return window.location.href.indexOf('isToy=true') > -1;
		},
	},
	created() {
		const getUrl = window.location,
			redirectKrUrl = this.$cookie.get('reDirectKR');

		// almost arena nexon website is https://*.nexon.com/${locale}/* ,
		// but maplestory website has been another structures. https://test-www.nexon.com/maplestory/*
		const documentLang = document.querySelector('html').lang || 'en';
		const documentArenaShortLang = documentLang.replace(/^(.{2}-)/, '').toLowerCase();
		const langPath = getUrl.pathname.split('/')[2];
		this.setLang(langPath && langPath.length === 2 ? langPath : documentArenaShortLang);

		if (redirectKrUrl && this.region === 'global') {
			location.href = redirectKrUrl;
		}
	},
	mounted() {
		window.navVersion = `Current Version => ${process.env.VUE_APP_VERSION}`;

		this.observer = new MutationObserver((list) => {
			if (list && list.length > 0) {
				this.otPrefCenterText = list[list.length - 1].target.innerHTML;
			}
		});
		this.observer.observe(document.getElementById('otPrefCenter'), { subtree: true, childList: true, attributes: true });
	},
	unmounted() {
		this.observer && this.observer.disconnect();
	},
	methods: {
		close() {
			this.showLanguage = false;
		},
		toggleGlobalLocale() {
			this.showGlobalLocale = !this.showGlobalLocale;
		},
		toggle() {
			this.showLanguage = !this.showLanguage;
			this.showGlobalLocale = false;
		},
		select() {
			this.toggle();
		},
		setLang(lang) {
			this.$i18n.locale = lang;
		},
		changeLanguage(locale, language) {
			const pathnames = location.pathname.split('/');
			pathnames[2] = locale;

			// Tracking
			dataLayer.push({
				event: 'Selected Language',
				languageSelected: language, //The language name in English
			});
			try {
				if (window.App && window.App.$router) {
					this.setLang(locale);
					window.Gnb.$i18n.locale = locale;
					this.toggle();
					return window.App.$router.replace({ params: { locale: locale }, query: { ...window.App.$route.query } });
				}
			} catch (e) {
				console.error(e);
			}
			this.$cookie.set('i18next', locale);
			location.href = `${location.protocol}//${location.host}${pathnames.join('/')}${location.search}`;
		},
		changeRegion(regionHost, locale, region) {
			// Tracking
			dataLayer.push({
				event: 'Selected Region',
				regionSelected: region, //Region name in English
			});

			location.href = `${regionHost}/main/${locale}`;
		},
		redirectToKR() {
			if (this.region === 'global') {
				this.$cookie.set('reDirectKR', this.externalRegionLinks.kr, { expires: '30D' });
			}
			window.open(this.externalRegionLinks.kr);
		},
		findValueByPrefix(object, prefix) {
			const keys = Object.keys(object),
				key = keys.find((k) => k.startsWith(prefix));
			return object[key];
		},
		camelize(str) {
			if (!str) return '';
			return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function(match, index) {
				if (+match === 0) return ''; // or if (/\s+/.test(match)) for white spaces
				return index === 0 ? match.toLowerCase() : match.toUpperCase();
			});
		},
		footerLinkTracking(linkName) {
			dataLayer.push({
				event: 'Footer Link Click',
				footerLinkName: linkName, //The link name in English
			});
		},
	},
};
</script>

<style lang="less" scoped>
@import '~@arena/toolkit/src/less/toolkit/toolkit.less';
#ot-pref-center.hidden {
	display: none;
}
.tw.zh {
	font-family: '微軟正黑體', 'Microsoft JhengHei', Arial, Helvetica, sans-serif;
}
.footer {
	&.hideFooter {
		display: none !important;
	}
	font-family: @font-family-base;
	&.tw.zh {
		font-family: '微軟正黑體', 'Microsoft JhengHei', Arial, Helvetica, sans-serif;
	}
	background-color: #191a1c;
	font-size: 12px;
	width: 100%;
	min-width: 375px;
	.footer-top {
		position: relative;
		height: auto;
		min-width: 150px;
		padding-left: 0;
		border-bottom: 1px solid #000000;
		.language {
			position: relative;
			color: #dcdcdc;
			width: 100%;
			height: 50px;
			span {
				vertical-align: top;
				display: inline-block;
			}
			ul {
				margin-bottom: 0;
			}
			.current-language {
				position: relative;
				text-align: center;
				line-height: 50px;
				cursor: pointer;
				.icon-locale {
					width: 13px;
					height: 13px;
					margin-right: 13px;
					margin-top: 18px;
					opacity: 0.8;
					transition: opacity 0.2s ease;
				}
				.icon-footer-arrow {
					width: 13px;
					height: 13px;
					margin-left: 13px;
					margin-top: 18px;
					opacity: 0.8;
					transition: opacity 0.2s ease;
				}
				&:hover {
					.icon-locale,
					.icon-footer-arrow {
						opacity: 1;
					}
				}
			}

			// language animation
			.slide-enter-active,
			.slide-leave-active {
				transition: 0.2s ease-out;
			}
			.slide-enter-to,
			.slide-leave {
				max-height: 294px;
				overflow: hidden;
			}
			.slide-enter,
			.slide-leave-to {
				overflow: hidden;
				max-height: 0;
			}
			.languages {
				position: absolute;
				z-index: 9999;
				width: 207px;
				bottom: 47px;
				left: 50%;
				transform: translate(-50%, 0%);
				background-color: #222223;
				border-radius: 5px;
				box-shadow: 0px 0px 24px 0 rgba(0, 0, 0, 0.6);

				// global locale animation
				.slide-enter-to,
				.slide-leave {
					max-height: 296px;
				}
				.global-locale {
					padding-top: 8px;
					padding-bottom: 8px;
					background-color: #1d1d1e;
				}
				li {
					position: relative;
					line-height: 2em;
					transition: background-color 0.2s ease;
					a {
						color: #ffffff;
						display: block;
						padding: 2px 15px;
						cursor: pointer;
						opacity: 0.8;
						transition: opacity 0.2s ease;
						&:hover {
							opacity: 1;
						}
					}
					&.region {
						position: relative;
						text-transform: uppercase;
						a {
							line-height: 18px;
							text-align: center;
							padding-top: 16px;
							padding-bottom: 15px;
						}
						.icon-plus,
						.icon-minus {
							width: 9px;
							height: 9px;
							opacity: 0.8;
							transition: opacity 0.2s ease;
							margin-left: 2px;
						}
						.icon-btn-link {
							width: 12px;
							height: 10px;
							opacity: 0.8;
							transition: opacity 0.2s ease;
							margin-left: 2px;
						}
						&:hover {
							&.global {
								.icon-minus,
								.icon-plus {
									opacity: 1;
								}
							}
							&.kr,
							&.jp {
								.icon-btn-link {
									opacity: 1;
								}
							}
						}
					}
					&.language-list {
						background-color: #1d1d1e;
						text-align: center;
						height: 28px;
					}
					&:hover {
						background-color: #0077ff;
					}
					&:hover:first-child:not(.language-list) {
						border-radius: 5px 5px 0px 0px;
					}
					&:hover:last-child:not(.language-list) {
						border-radius: 0px 0px 5px 5px;
					}
					&:active {
						opacity: 0.5;
					}
				}
			}
		}
	}
	.footer-body {
		position: relative;
		min-height: 148px;
		width: 100%;
		text-align: center;
		.links {
			a {
				font-size: 11px;
				cursor: pointer;
				color: #fff;
				opacity: 0.8;
				transition: opacity 0.2s ease;
				&:hover {
					opacity: 1;
				}
			}
			& > li + li:before {
				padding: 0 15px 0 10px;
				color: #3c3c3c;
				content: '|\A0';
			}
		}
		.links-top {
			padding-top: 28px;
			margin: 0px;
		}
		.links-bottom {
			padding-top: 10px;
			margin: 0px;
			a {
				color: #7e7e7f;
			}
		}
		.nexon-logo-footer {
			padding-top: 24px;
			padding-bottom: 21px;
			.logo {
				width: 90px;
				height: 26px;
			}
		}
	}
	.footer-bottom {
		position: relative;
		height: 60px;
		background-color: #141516;
		.copyright {
			color: rgba(102, 102, 102, 0.5);
			text-align: center;
			padding-top: 22px;
		}
	}
}
</style>
